import { LISTING_TYPES } from '../constants';
import site from '../seo/site.json';
import { normalizeProductType, resolveProductTypeSectionKey } from './catalogSections';

export { normalizeProductType } from './catalogSections';

/**
 * Returns true if the product listing can be bought/downloaded natively.
 * @param {any} product Product document.
 * @returns {boolean}
 */
export function isNativeListing(product) {
  return product?.listingType === LISTING_TYPES.NATIVE || product?.listingType === LISTING_TYPES.FREE;
}

/**
 * Returns true when a listing is external and should link out of Audio at Play.
 * @param {any} product Product document.
 * @returns {boolean}
 */
export function isExternalListing(product) {
  return (
    product?.listingType === LISTING_TYPES.LEGACY_EXTERNAL ||
    product?.listingType === LISTING_TYPES.EXTERNAL_MARKETPLACE ||
    product?.listingType === LISTING_TYPES.LINK_ONLY
  );
}

const PRODUCT_TYPE_SECTION_DEFINITIONS = site.sections;

const DOWNLOAD_COUNT_ELIGIBLE_TYPE_KEYS = new Set([
  'singlesound',
  'singlesounds',
  'oneshot',
  'oneshots',
  'singlesfx',
  'soundpack',
  'soundpacks',
  'samplepack',
  'samplepacks',
  'abletonpack',
  'abletonpacks',
  'abletonsoundpack',
  'abletonsoundpacks',
]);

/**
 * Supported file type options shown in product metadata.
 */
export const PRODUCT_FILE_TYPE_OPTIONS = [
  { value: 'wav', label: '.WAV' },
  { value: 'mp3', label: '.MP3' },
  { value: 'flac', label: '.FLAC' },
  { value: 'aiff', label: '.AIFF' },
  { value: 'ogg', label: '.OGG' },
  { value: 'm4a', label: '.M4A' },
  { value: 'aac', label: '.AAC' },
  { value: 'mid', label: '.MID' },
  { value: 'als', label: '.ALS' },
  { value: 'adg', label: '.ADG' },
];

const PRODUCT_FILE_TYPE_LABEL_BY_VALUE = new Map(
  PRODUCT_FILE_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

/**
 * Normalizes a single file type value to a canonical key.
 * @param {string} fileType File type value.
 * @returns {string}
 */
function normalizeIncludedFileType(fileType) {
  return String(fileType || '')
    .trim()
    .toLowerCase()
    .replace(/^\./, '');
}

/**
 * Exposes product-type section metadata for page rendering.
 */
export const PRODUCT_TYPE_SECTIONS = PRODUCT_TYPE_SECTION_DEFINITIONS.map((section) => ({
  key: section.key,
  title: section.title,
  description: section.description,
}));

/**
 * Returns true when native download count should be shown for this product.
 * Scoped to native single sounds and sound packs.
 * @param {any} product Product document.
 * @returns {boolean}
 */
export function shouldShowNativeDownloadCount(product) {
  if (!isNativeListing(product)) {
    return false;
  }
  return DOWNLOAD_COUNT_ELIGIBLE_TYPE_KEYS.has(normalizeProductType(product?.productType));
}

/**
 * Returns normalized native download count value for a product.
 * @param {any} product Product document.
 * @returns {number}
 */
export function getNativeDownloadCount(product) {
  const count = Number(product?.nativeDownloadCount || 0);
  if (!Number.isFinite(count) || count < 0) {
    return 0;
  }
  return Math.floor(count);
}

/**
 * Groups products into standardized product-type sections.
 * @param {any[]} products Product records.
 * @param {{includeEmpty?: boolean, preferFeatured?: boolean, maxProductsPerSection?: number}} options Grouping options.
 * @returns {{key: string, title: string, description: string, products: any[]}[]}
 */
export function groupProductsByTypeSections(products, options = {}) {
  const includeEmpty = options.includeEmpty !== false;
  const preferFeatured = options.preferFeatured === true;
  const maxProductsPerSection =
    Number.isInteger(options.maxProductsPerSection) && options.maxProductsPerSection > 0
      ? options.maxProductsPerSection
      : null;

  const groupedProducts = PRODUCT_TYPE_SECTION_DEFINITIONS.reduce((accumulator, section) => {
    accumulator[section.key] = [];
    return accumulator;
  }, {});

  for (const product of products || []) {
    const sectionKey = resolveProductTypeSectionKey(normalizeProductType(product?.productType));
    groupedProducts[sectionKey].push(product);
  }

  const sections = PRODUCT_TYPE_SECTION_DEFINITIONS.map((section) => {
    const matchingProducts = groupedProducts[section.key] || [];
    const featuredProducts = matchingProducts.filter((product) => product?.isHomepageFeatured === true);
    const sourceProducts = preferFeatured && featuredProducts.length ? featuredProducts : matchingProducts;
    const selectedProducts = maxProductsPerSection ? sourceProducts.slice(0, maxProductsPerSection) : sourceProducts;

    return {
      key: section.key,
      title: section.title,
      description: section.description,
      products: selectedProducts,
    };
  });

  return includeEmpty ? sections : sections.filter((section) => section.products.length);
}

/**
 * Normalizes and filters included file type values.
 * @param {any[]} fileTypes Raw file type values.
 * @returns {string[]}
 */
export function normalizeIncludedFileTypes(fileTypes) {
  const values = Array.isArray(fileTypes) ? fileTypes : [];
  const seen = new Set();
  const normalizedValues = [];

  for (const value of values) {
    const normalizedValue = normalizeIncludedFileType(value);
    if (!normalizedValue) {
      continue;
    }
    if (!PRODUCT_FILE_TYPE_LABEL_BY_VALUE.has(normalizedValue)) {
      continue;
    }
    if (seen.has(normalizedValue)) {
      continue;
    }

    seen.add(normalizedValue);
    normalizedValues.push(normalizedValue);
  }

  return normalizedValues;
}

/**
 * Returns a display label for a product file type.
 * @param {string} fileType File type value.
 * @returns {string}
 */
export function fileTypeLabel(fileType) {
  const normalizedValue = normalizeIncludedFileType(fileType);
  return PRODUCT_FILE_TYPE_LABEL_BY_VALUE.get(normalizedValue) || String(fileType || '').trim();
}

/**
 * Provides a readable badge label for listing type.
 * @param {string} listingType Listing type value.
 * @returns {string}
 */
export function listingTypeLabel(listingType) {
  switch (listingType) {
    case LISTING_TYPES.NATIVE:
      return 'Native';
    case LISTING_TYPES.FREE:
      return 'Free';
    case LISTING_TYPES.LEGACY_EXTERNAL:
      return 'Legacy External';
    case LISTING_TYPES.EXTERNAL_MARKETPLACE:
      return 'External Marketplace';
    case LISTING_TYPES.LINK_ONLY:
      return 'Link Only';
    default:
      return 'Unknown';
  }
}

/**
 * Provides a readable badge label for product type.
 * @param {string} productType Product type value.
 * @returns {string}
 */
export function productTypeLabel(productType) {
  switch (normalizeProductType(productType)) {
    case 'soundpack':
    case 'soundpacks':
    case 'samplepack':
    case 'samplepacks':
      return 'Sound Pack';
    case 'singlesound':
    case 'singlesounds':
    case 'oneshot':
    case 'oneshots':
      return 'Single Sound';
    case 'instrument':
    case 'instruments':
      return 'Instrument';
    case 'abletonpack':
    case 'abletonpacks':
    case 'abletonsoundpack':
    case 'abletonsoundpacks':
      return 'Ableton Sound Pack';
    case 'm4l':
    case 'm4linstrument':
    case 'm4ldevice':
    case 'm4ldevices':
    case 'maxforlive':
      return 'M4L Device';
    default: {
      const normalized = String(productType || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
      return normalized || 'Unknown Type';
    }
  }
}
